import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SearchX, ChevronLeft, RefreshCw, ChevronRight } from "lucide-react";

const CHANNEL_LABELS = {
  efectivo_bbva: "Efectivo BBVA",
  tarjeta_clip: "Tarjeta CLIP",
  rappi: "Rappi",
  uber_eats: "Uber Eats",
  didi: "DiDi",
  oxxo_pay: "Oxxo Pay",
};

export default function OpsFaltantes() {
  const navigate = useNavigate();
  const [data, setData] = useState({ total_missing: 0, count: 0, items: [] });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  // Fix WH16 (2026-08-31): Claudia pidio que los faltantes salgan solos de la
  // venta del dia contra el corte (video 25/08, min 17:04). El endpoint
  // /ops/sales/pos-vs-corte ya existia y no lo veia nadie: no tenia pantalla.
  // Se agrega como segunda pestana; la conciliacion bancaria queda intacta.
  const [tab, setTab] = useState("banco");
  const [pos, setPos] = useState({ rows: [], totals: null });
  const [posLoading, setPosLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterBranch) params.branch_id = filterBranch;
      if (filterChannel) params.channel = filterChannel;
      const [missingRes, branchRes] = await Promise.all([
        api.get("/ops/reconciliations/missing", { params }),
        branches.length ? Promise.resolve({ data: branches }) : api.get("/branches"),
      ]);
      setData(missingRes.data);
      if (!branches.length) setBranches(branchRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterChannel, branches]);

  const fetchPos = useCallback(async () => {
    setPosLoading(true);
    try {
      const params = {};
      if (filterBranch) params.branch_id = filterBranch;
      const d = new Date();
      params.to_date = d.toISOString().slice(0, 10);
      d.setDate(d.getDate() - 30);
      params.from_date = d.toISOString().slice(0, 10);
      const res = await api.get("/ops/sales/pos-vs-corte", { params });
      setPos(res.data || { rows: [], totals: null });
    } catch (err) {
      console.error(err);
      setPos({ rows: [], totals: null });
    } finally {
      setPosLoading(false);
    }
  }, [filterBranch]);

  useEffect(() => { fetchData(); }, [filterBranch, filterChannel]);
  useEffect(() => { if (tab === "pos") fetchPos(); }, [tab, filterBranch]);

  const fmtMXN = v => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
  // Fix WH17 (2026-08-31): `pos.totals` que devuelve el endpoint suma TODOS los
  // días del rango, incluidos los que todavía no tienen corte cargado. Medido en
  // producción el 31/08: 104 de 120 días-sucursal están en "sin_corte", así que
  // la tarjeta pintaba "Diferencia $3,855,675" justo al lado de "0 días con
  // diferencia" y del cartel "Sin diferencias entre POS y corte". Un agujero de
  // $3.8M que no existe, en la pantalla que la dirección va a mirar primero.
  // Ahora los tres montos se calculan SÓLO sobre los días comparables (los que
  // tienen POS y corte), y los días sin corte pasan a ser su propio indicador,
  // que es la información que de verdad importa hoy.
  const posRows = pos.rows || [];
  const posComparables = posRows.filter(r => r.status !== "sin_corte" && r.status !== "sin_pos");
  const posSinCorte = posRows.filter(r => r.status === "sin_corte");
  const posConDiff = posComparables.filter(r => r.status === "faltante" || r.status === "sobrante");
  const posTot = posComparables.reduce(
    (a, r) => ({
      pos: a.pos + (r.pos_total || 0),
      corte: a.corte + (r.corte_total || 0),
      diff: a.diff + (r.diff || 0),
    }),
    { pos: 0, corte: 0, diff: 0 },
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="ops-faltantes-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ops/conciliacion")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <SearchX className="w-6 h-6 text-red-500" /> Faltantes
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Diferencias detectadas en conciliaciones con alerta o crítico, por sucursal, canal y día</p>
        </div>
      </div>

      {/* Fix WH16 (2026-08-31): dos formas de mirar el mismo faltante. */}
      <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 w-fit">
        {[["banco", "Banco vs corte"], ["pos", "Venta POS vs corte"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`faltantes-tab-${v}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${tab === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "banco" && (
        <>
      {/* Fix WH22 (2026-08-31): "Total faltante" sumaba dias que no se pudieron
          comparar por falta de extracto bancario. Con el modulo Bancos sin
          conexion viva, eso pintaba $23.998 en rojo de plata que nunca falto.
          Ahora los dias sin extracto tienen su propio indicador, en gris. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-2xl font-bold text-red-600" data-testid="faltantes-total">{fmtMXN(data.total_missing)}</div>
          <div className="text-xs text-slate-500 mt-1">Total faltante</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-2xl font-bold text-slate-900">{data.count}</div>
          <div className="text-xs text-slate-500 mt-1">Registros</div>
        </div>
        <div className={`rounded-xl border p-4 ${(data.sin_conciliar_count || 0) > 0 ? "bg-slate-50 border-slate-300" : "bg-white border-slate-200"}`}>
          <div className="text-2xl font-bold text-slate-600" data-testid="faltantes-sin-conciliar">{data.sin_conciliar_count || 0}</div>
          <div className="text-xs text-slate-500 mt-1">Días sin extracto bancario</div>
        </div>
      </div>

      {(data.sin_conciliar_count || 0) > 0 && (
        <div className="bg-slate-50 border border-slate-300 rounded-xl p-4 text-sm text-slate-700" data-testid="faltantes-aviso-sin-extracto">
          <span className="font-semibold">
            {data.sin_conciliar_count} día{data.sin_conciliar_count === 1 ? "" : "s"} por {fmtMXN(data.sin_conciliar_total)} no se pudieron conciliar
          </span>{" "}
          porque no hay movimientos bancarios importados de esas fechas.
          <span className="font-semibold"> Eso no es plata faltante</span>: es que falta el extracto.
          Quedan fuera del total de arriba a propósito.
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <select value={filterChannel} onChange={e => setFilterChannel(e.target.value)}
          data-testid="faltantes-filter-channel" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">Todos los canales</option>
          {Object.entries(CHANNEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterBranch} onChange={e => setFilterBranch(e.target.value)}
          data-testid="faltantes-filter-branch" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">Todas las sucursales</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <SearchX className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Sin faltantes detectados</p>
          <p className="text-sm mt-1">Todas las conciliaciones recientes están OK.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold text-slate-600">Día</th>
                <th className="px-4 py-2 font-semibold text-slate-600">Sucursal</th>
                <th className="px-4 py-2 font-semibold text-slate-600">Canal</th>
                <th className="px-4 py-2 font-semibold text-slate-600 text-right">Monto faltante</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={i} data-testid={`faltante-row-${i}`}
                  onClick={() => navigate(`/ops/conciliacion/${it.reconciliation_id}`)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-4 py-2 text-slate-700">{it.date}</td>
                  <td className="px-4 py-2 text-slate-700">{it.branch_name}</td>
                  <td className="px-4 py-2">{CHANNEL_LABELS[it.channel] || it.channel}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-600">{fmtMXN(it.missing_amount)}</td>
                  <td className="px-4 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}

      {tab === "pos" && (
        <>
          <p className="text-sm text-slate-500 -mt-2">
            Últimos 30 días. Compara la venta que reporta el POS (Wansoft) contra el corte de caja firmado por el gerente.
            Una diferencia positiva es un <span className="font-semibold text-red-600">faltante</span>: el POS vendió más de lo que declara el corte.
          </p>
          <div className="flex gap-3 flex-wrap">
            <select value={filterBranch} onChange={e => setFilterBranch(e.target.value)}
              data-testid="faltantes-pos-filter-branch" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
              <option value="">Todas las sucursales</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xl font-bold text-slate-900">{fmtMXN(posTot.pos)}</div>
              <div className="text-xs text-slate-500 mt-1">Venta POS <span className="text-slate-400">(días comparables)</span></div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xl font-bold text-slate-900">{fmtMXN(posTot.corte)}</div>
              <div className="text-xs text-slate-500 mt-1">Declarado en cortes</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className={`text-xl font-bold ${Math.abs(posTot.diff) > 50 ? "text-red-600" : "text-slate-900"}`} data-testid="faltantes-pos-diff">{fmtMXN(posTot.diff)}</div>
              <div className="text-xs text-slate-500 mt-1">Diferencia</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xl font-bold text-slate-900" data-testid="faltantes-pos-condiff">{posConDiff.length}</div>
              <div className="text-xs text-slate-500 mt-1">Días con diferencia</div>
            </div>
            {/* Fix WH17: con 86% de los días sin corte, éste es el número que hay
                que mirar. Se enlaza a Cortes para poder generarlos. */}
            <div className={`rounded-xl border p-4 ${posSinCorte.length > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
              <div className={`text-xl font-bold ${posSinCorte.length > 0 ? "text-amber-700" : "text-slate-900"}`} data-testid="faltantes-pos-sincorte">{posSinCorte.length}</div>
              <div className="text-xs text-slate-500 mt-1">Días sin corte cargado</div>
            </div>
          </div>

          {posSinCorte.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="faltantes-pos-aviso-sincorte">
              <span className="font-semibold">{posSinCorte.length} de {posRows.length} días-sucursal no tienen corte cargado</span> en este rango,
              así que quedan fuera de la comparación: sin corte no hay contra qué comparar la venta del POS.
              Los cortes que faltan se pueden generar desde el POS en{" "}
              <button onClick={() => navigate("/ops/cortes")} className="font-semibold underline" data-testid="faltantes-pos-ir-cortes">Cortes de caja</button>.
            </div>
          )}

          {posLoading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : posComparables.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <SearchX className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Todavía no hay días para comparar</p>
              <p className="text-sm mt-1">Ningún día del rango tiene venta del POS y corte al mismo tiempo.</p>
            </div>
          ) : posConDiff.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <SearchX className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sin diferencias entre POS y corte</p>
              <p className="text-sm mt-1">Los {posComparables.length} días con corte cargado cuadran con la venta del POS.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-semibold text-slate-600">Día</th>
                    <th className="px-4 py-2 font-semibold text-slate-600">Sucursal</th>
                    <th className="px-4 py-2 font-semibold text-slate-600 text-right">Venta POS</th>
                    <th className="px-4 py-2 font-semibold text-slate-600 text-right">Corte</th>
                    <th className="px-4 py-2 font-semibold text-slate-600 text-right">Diferencia</th>
                    <th className="px-4 py-2 font-semibold text-slate-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {posConDiff.map((r, i) => (
                    <tr key={`${r.branch_id}-${r.date}`} data-testid={`faltante-pos-row-${i}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-2 text-slate-700">{r.date}</td>
                      <td className="px-4 py-2 text-slate-700">{r.branch_name}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmtMXN(r.pos_total)}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmtMXN(r.corte_total)}</td>
                      <td className={`px-4 py-2 text-right font-mono font-semibold ${r.diff > 0 ? "text-red-600" : "text-amber-600"}`}>
                        {r.diff > 0 ? "+" : ""}{fmtMXN(r.diff)}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${r.status === "faltante" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.status === "faltante" ? "Faltante" : "Sobrante"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
